import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion, useScroll, useSpring, useTransform, useMotionValueEvent } from "framer-motion";
import {
  Eye, Lock, Compass,
  ArrowRight, Mail, User, KeyRound, EyeOff, Loader2, Menu, X as XIcon,
  Newspaper, Target, MapPin, RotateCcw, MessagesSquare, ClipboardCheck, CheckCircle2,
  HelpCircle, ChevronDown,
} from "lucide-react";
import { XamseenLogo } from "@/components/xamseen-logo";
import { GoogleMark, AppleMark } from "@/components/auth-shell";
import { HeroPhone, SCREEN_ICON, type ScreenKey } from "@/components/hero-phone";
import { Typewriter } from "@/components/typewriter";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { cn } from "@/lib/utils";
import { IMPACT_STORIES, type ImpactStory, fetchPublishedImpactStories } from "@/lib/impact-stories";
import {
  consumeOAuthErrorFromUrl,
  describeOAuthError,
  type OAuthFailure,
} from "@/lib/oauth-errors";

import docReport from "@/assets/landing/doc-report.jpg";
import docMeeting from "@/assets/landing/doc-meeting.jpg";
import docStreet from "@/assets/landing/doc-street.jpg";
import docCleanup from "@/assets/landing/doc-cleanup.jpg";
import docTransit from "@/assets/landing/doc-transit.jpg";
import docRepair from "@/assets/landing/doc-repair.jpg";

/**
 * The public front door: a monochrome, product-led marketing page shown to
 * visitors who are not signed in. Members go straight to Lens and never see it.
 */
export function PublicLanding() {
  return (
    <div className="dark min-h-screen w-full overflow-x-clip bg-black text-foreground">
      <PublicHeader />
      <main>
        {/* Cinematic black: the product moment. */}
        <Hero />
        <ProductStory />

        {/* White: what you can do, read as an editorial spread. */}
        <Band tone="paper">
          <Capabilities />
        </Band>

        {/* Black: the reporting and response path. */}
        <Band tone="ink">
          <Journey />
        </Band>

        {/* White: people and community photography. */}
        <Band tone="paper">
          <StoriesSection />
        </Band>

        {/* Black: public work and outcomes. */}
        <Band tone="ink">
          <ImpactStoriesSection />
        </Band>

        {/* White: calm, highly readable. */}
        <Band tone="paper-soft">
          <Principle />
          <FaqSection />
        </Band>

        <FinalCta />
      </main>
      <PublicFooter />
    </div>
  );
}

/**
 * A tonal band. Light bands re-map the page's white-on-black utilities onto
 * near-black ink on white (see `.on-paper` in styles.css) and fade in as they
 * meet the reader, so the switch between black and white feels deliberate.
 */
function Band({
  tone,
  children,
}: {
  tone: "ink" | "paper" | "paper-soft";
  children: React.ReactNode;
}) {
  const reduced = useReducedMotion();
  const paper = tone !== "ink";
  return (
    <motion.section
      initial={reduced ? undefined : { opacity: 0 }}
      whileInView={reduced ? undefined : { opacity: 1 }}
      viewport={{ once: true, margin: "0px 0px -8% 0px" }}
      transition={{ duration: 0.6, ease: EASE }}
      className={cn(
        "relative",
        paper && "on-paper",
        tone === "paper-soft" && "on-paper-soft",
        tone === "ink" && "bg-black",
      )}
    >
      {children}
    </motion.section>
  );
}


/* --------------------------------- Motion --------------------------------- */

const EASE = [0.22, 0.61, 0.36, 1] as const;

/** Calm fade + rise on scroll into view. */
function Rise({
  children,
  delay = 0,
  className,
  as = "div",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  as?: "div" | "section" | "li" | "header";
}) {
  const reduced = useReducedMotion();
  const M = motion[as];
  return (
    <M
      initial={reduced ? undefined : { opacity: 0, y: 18 }}
      whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -12% 0px" }}
      transition={{ duration: 0.7, delay: delay / 1000, ease: EASE }}
      className={className}
    >
      {children}
    </M>
  );
}

/** Icon medallion that springs in on scroll — the shared step marker of the page. */
function Medallion({
  icon: Icon,
  delay = 0,
  size = "md",
  className,
}: {
  icon: typeof Eye;
  delay?: number;
  size?: "sm" | "md";
  className?: string;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.span
      initial={reduced ? undefined : { scale: 0.55, opacity: 0 }}
      whileInView={reduced ? undefined : { scale: 1, opacity: 1 }}
      viewport={{ once: true, amount: 0.6 }}
      transition={{ type: "spring", stiffness: 250, damping: 20, delay }}
      className={cn(
        "grid shrink-0 place-items-center text-white/75 transition-colors duration-500",
        size === "sm" ? "h-9 w-9 rounded-xl" : "h-11 w-11",
        className,
      )}
    >
      <Icon className={size === "sm" ? "h-4 w-4" : "h-[18px] w-[18px]"} strokeWidth={1.7} />
    </motion.span>
  );
}

/** Section eyebrow + heading, set in type alone. */
function SectionHead({
  icon,
  eyebrow,
  title,
  children,
  className,
}: {
  icon: typeof Eye;
  eyebrow: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="flex items-center gap-3">
        <Medallion icon={icon} size="sm" />
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/60">{eyebrow}</p>
      </div>
      <Rise delay={80}>
        <h2 className="mt-6 max-w-2xl font-display text-[clamp(2rem,7vw,3.4rem)] font-black leading-[1.05] tracking-[-0.025em]">
          {title}
        </h2>
        {children}
      </Rise>
    </div>
  );
}


/* --------------------------------- Header --------------------------------- */

const NAV = [
  { href: "#capabilities", label: "About" },
  { href: "#journey", label: "How it works" },
  { href: "#impact", label: "Impact" },
  { href: "#stories", label: "Stories" },
  { href: "#faq", label: "FAQ" },
];

function PublicHeader() {
  const [open, setOpen] = useState(false);
  return (
    <>
    <header
      className="sticky top-0 z-40 bg-black/85 backdrop-blur-xl"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div
        className="mx-auto flex h-16 w-full max-w-7xl items-center gap-3 sm:h-20"
        style={{
          paddingLeft: "max(1rem, env(safe-area-inset-left))",
          paddingRight: "max(1rem, env(safe-area-inset-right))",
        }}
      >
        {/* Logo — anchored left */}
        <Link to="/" aria-label="Xamseen home" className="flex min-h-11 shrink-0 items-center">
          <XamseenLogo variant="white" className="h-5 sm:h-6" />
        </Link>

        <nav aria-label="Public navigation" className="ml-8 hidden flex-1 items-center gap-8 lg:flex">
          {NAV.map((n) => (
            <a
              key={n.label}
              href={n.href}
              className="inline-flex min-h-11 items-center text-sm text-white/60 transition-colors hover:text-white"
            >
              {n.label}
            </a>
          ))}
        </nav>

        {/* Right cluster — CTA then hamburger at the far right */}
        <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
          <Link
            to="/signin"
            className="hidden h-11 items-center rounded-full border border-white/15 px-5 text-sm font-medium text-white transition-colors hover:bg-white/10 sm:inline-flex"
          >
            Sign in
          </Link>
          <motion.div whileTap={{ scale: 0.97 }} className="min-w-0">
            <Link
              to="/signup"
              className="inline-flex h-11 items-center whitespace-nowrap rounded-full bg-white px-3.5 text-[13px] font-semibold text-black transition-opacity hover:opacity-90 sm:px-5 sm:text-sm"
            >
              <span className="hidden xs:inline">Create account</span>
              <span className="xs:hidden">Join</span>
            </Link>
          </motion.div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-expanded={open}
            aria-label="Open menu"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-white/15 text-white lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>

        </div>
      </div>
    </header>

      <AnimatePresence>
        {open && (
          <>

            <motion.div
              key="scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-40 bg-black/70 lg:hidden"
            />
            <motion.nav
              key="drawer"
              aria-label="Mobile navigation"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.35, ease: EASE }}
              className="fixed right-0 top-0 z-50 flex h-full w-[82%] max-w-xs flex-col gap-1 border-l border-white/10 bg-[#070707] p-5 lg:hidden"
              style={{ paddingTop: "calc(env(safe-area-inset-top) + 1.25rem)" }}
            >
              <div className="mb-4 flex items-center justify-between">
                <XamseenLogo variant="white" className="h-5" />
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>
              {NAV.map((n) => (
                <a
                  key={n.label}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="flex min-h-12 items-center text-[15px] text-white/75"
                >
                  {n.label}
                </a>
              ))}
              <Link
                to="/signin"
                onClick={() => setOpen(false)}
                className="mt-4 inline-flex min-h-12 items-center justify-center rounded-xl border border-white/15 text-[15px] font-medium text-white"
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex min-h-12 items-center justify-center rounded-xl bg-white text-[15px] font-semibold text-black"
              >
                Join Xamseen
              </Link>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/* ---------------------------------- Hero ---------------------------------- */

function Hero() {
  const reduced = useReducedMotion();
  const seq = (delay: number) => ({
    initial: reduced ? undefined : { opacity: 0, y: 20 },
    animate: reduced ? undefined : { opacity: 1, y: 0 },
    transition: { duration: 0.75, delay, ease: EASE },
  });

  return (
    <section className="relative overflow-hidden">
      <div className="mx-auto w-full max-w-7xl px-4 pb-14 pt-14 sm:px-8 sm:pt-24">
        <motion.h1
          {...seq(0.05)}
          className="max-w-4xl font-display text-[clamp(2.4rem,9vw,5rem)] font-black uppercase leading-[0.98] tracking-[-0.035em]"
        >
          See it. Share it. Shape it.
        </motion.h1>

        <motion.div
          {...seq(0.16)}
          className="mt-7 max-w-xl font-display text-[clamp(1.15rem,4.5vw,1.7rem)] font-semibold leading-[1.25] tracking-[-0.015em] text-white/85"
        >
          <p>Communities see the problems.</p>
          <Typewriter
            className="mt-1"
            phrases={[
              "Together, we shape solutions.",
              "Together, we get things fixed.",
            ]}
          />
        </motion.div>

        <motion.p {...seq(0.26)} className="mt-6 max-w-lg text-[15px] leading-[1.75] text-white/65 sm:text-base">
          Share what you see around you, talk about it with people nearby, and follow what happens next.
        </motion.p>

        <motion.div {...seq(0.36)} className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
          <motion.div whileHover={reduced ? undefined : { y: -2 }} whileTap={{ scale: 0.985 }}>
            <Link
              to="/signup"
              className="group inline-flex h-14 w-full items-center justify-between gap-6 rounded-2xl bg-white px-6 text-base font-semibold text-black sm:w-auto"
            >
              Join Xamseen
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
          <a
            href="#journey"
            className="inline-flex h-14 items-center justify-center rounded-2xl border border-white/15 px-6 text-base font-medium text-white transition-colors hover:bg-white/[0.06]"
          >
            See how it works
          </a>
        </motion.div>
      </div>




      <div className="mx-auto grid w-full max-w-7xl grid-cols-3 gap-3 px-4 pb-16 sm:px-8 sm:pb-24">
        <Photo src={docStreet} alt="A man checking his phone on a busy street in Hanoi, Vietnam" className="h-28 sm:h-44" />
        <Photo src={docTransit} alt="Commuters waiting at a bus shelter in Chicago on a winter morning" className="h-28 sm:h-44" delay={90} />
        <Photo src={docCleanup} alt="Residents clearing a vacant lot together in Amman, Jordan" className="h-28 sm:h-44" delay={180} />
      </div>
    </section>
  );
}

/* ------------------------------ Product story ----------------------------- */

const STAGES: { key: ScreenKey; kicker: string; title: string; body: string }[] = [
  {
    key: "lens",
    kicker: "Lens",
    title: "See what's happening.",
    body: "See what people around you are talking about — flooding after heavy rain, a light that has been out for weeks, a recycling point that just opened.",
  },
  {
    key: "report",
    kicker: "Report",
    title: "See something? Share it.",
    body: "Report an issue in a minute: write what you saw, add a photo, video, voice note or document, and set how precisely the place is shared.",
  },
  {
    key: "map",
    kicker: "Map",
    title: "See where it keeps happening.",
    body: "Reports land on the map, so one report becomes a pattern you can point at — street, junction, neighbourhood.",
  },
  {
    key: "spaces",
    kicker: "Spaces",
    title: "Find people in your community.",
    body: "Talk about what matters where it matters. Conversations stay tied to the place they belong to.",
  },
  {
    key: "pulse",
    kicker: "Pulse",
    title: "See what your community is talking about.",
    body: "What is picking up, what is steady and what has gone quiet in your area.",
  },
  {
    key: "impact",
    kicker: "Impact",
    title: "See what changed.",
    body: "From a report to a response, then work in progress, then something finished the community can check for itself.",
  },
];

/**
 * The pinned narrative: the device stays with the reader while the story steps
 * through the real product surfaces. Reduced motion gets the same content as a
 * plain, unpinned list.
 */
function ProductStory() {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const [stage, setStage] = useState(0);
  const [dir, setDir] = useState<1 | -1>(1);
  const smooth = useSpring(scrollYProgress, { stiffness: 90, damping: 26, mass: 0.5 });
  const y = useTransform(smooth, [0, 1], [22, -22]);
  const rotate = useTransform(smooth, [0, 0.5, 1], [1.6, 0, -1.6]);
  const scale = useTransform(smooth, [0, 0.12, 0.88, 1], [0.965, 1, 1, 0.965]);
  const railScale = useTransform(smooth, [0, 1], [0, 1]);

  useMotionValueEvent(smooth, "change", (v) => {
    const next = Math.max(0, Math.min(STAGES.length - 1, Math.floor(v * STAGES.length + 0.0001)));
    setStage((s) => {
      if (s === next) return s;
      setDir(next > s ? 1 : -1);
      return next;
    });
  });


  if (reduced) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 pb-16 sm:px-8">
        <HeroPhone screen="lens" />
        <ul className="mt-10 grid gap-6 sm:grid-cols-2">
          {STAGES.map((s) => (
            <li key={s.key}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/60">{s.kicker}</p>
              <h3 className="mt-2 font-display text-xl font-bold tracking-[-0.01em]">{s.title}</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-white/65">{s.body}</p>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const active = STAGES[stage]!;

  return (
    <div ref={ref} className="relative" style={{ height: `${STAGES.length * 88 + 40}vh` }}>
      <div className="sticky top-0 flex min-h-screen items-center">
        <div className="mx-auto grid w-full max-w-7xl items-center gap-10 px-4 py-14 sm:px-8 lg:grid-cols-[1fr_minmax(0,0.85fr)] lg:gap-16">
          <div className="order-2 lg:order-1">
            <div className="relative h-[3px] w-full max-w-[13rem] overflow-hidden rounded-full bg-white/12">
              <motion.div
                aria-hidden
                className="h-full w-full origin-left rounded-full bg-white"
                style={{ scaleX: railScale }}
              />
            </div>
            <div className="mt-3 flex items-center gap-2">
              {STAGES.map((s, i) => (
                <motion.span
                  key={s.key}
                  aria-hidden
                  className="h-[3px] rounded-full bg-white"
                  animate={{ width: i === stage ? 32 : 12, opacity: i === stage ? 1 : 0.22 }}
                  transition={{ duration: 0.5, ease: EASE }}
                />
              ))}
            </div>

            <div className="mt-7 min-h-[15rem] sm:min-h-[14rem]">
              <AnimatePresence mode="wait" initial={false} custom={dir}>
                <motion.div
                  key={active.key}
                  custom={dir}
                  initial={{ opacity: 0, y: dir * 22, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: dir * -18, filter: "blur(6px)" }}
                  transition={{ duration: 0.5, ease: EASE }}
                >
                  <div className="flex items-center gap-3">
                    <Medallion icon={SCREEN_ICON[active.key]} size="sm" />
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/60">
                      {active.kicker}
                    </p>
                  </div>
                  <h2 className="mt-5 max-w-lg font-display text-[clamp(1.7rem,5.5vw,2.9rem)] font-black leading-[1.06] tracking-[-0.025em]">
                    {active.title}
                  </h2>
                  <p className="mt-5 max-w-md text-[15px] leading-[1.8] text-white/65">{active.body}</p>
                </motion.div>
              </AnimatePresence>
            </div>

          </div>

          <motion.div
            style={{ y, rotate, scale }}
            className="order-1 w-full [transform-style:preserve-3d] lg:order-2"
          >
            <HeroPhone screen={active.key} direction={dir} />
          </motion.div>

        </div>
      </div>
    </div>
  );
}


function Photo({
  src,
  alt,
  className,
  delay = 0,
  eager,
}: {
  src: string;
  alt: string;
  className?: string;
  delay?: number;
  eager?: boolean;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? undefined : { opacity: 0, scale: 1.03 }}
      whileInView={reduced ? undefined : { opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "0px 0px -10% 0px" }}
      transition={{ duration: 0.9, delay: delay / 1000, ease: EASE }}
      className={cn("overflow-hidden", className)}
    >
      <img
        src={src}
        alt={alt}
        width={1200}
        height={912}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        className="h-full w-full object-cover"
      />
    </motion.div>
  );
}

/* ------------------------------ Capabilities ------------------------------ */

const PILLARS = [
  { title: "See what's happening around you", body: "A feed and a map of what people are reporting nearby — from your street to the other side of the world." },
  { title: "See something? Share it", body: "Post it in a minute with photos, video, audio and a place, at the precision you choose." },
  { title: "Find people in your community", body: "Join Spaces, confirm what neighbours report and talk about what matters where you live." },
  { title: "Follow what happens next", body: "Follow a report from raised to resolved, with responses and outcomes kept in the open." },
];

const SURFACES = ["Lens", "Map", "Pulse", "Spaces", "Answers", "Projects", "Impact", "Nearby"];

function Capabilities() {
  return (
    <section id="capabilities" className="mx-auto w-full max-w-7xl scroll-mt-24 px-4 py-20 sm:px-8 sm:py-32">
      <SectionHead icon={Compass} eyebrow="What you can do" title="See it. Talk about it. Follow it through." />

      <div className="mt-16 grid gap-14 sm:mt-20 sm:grid-cols-2 sm:gap-x-16 sm:gap-y-20">
        {PILLARS.map((p, i) => (
          <Rise key={p.title} delay={i * 80}>
            <h3 className="max-w-sm font-display text-[clamp(1.35rem,4.5vw,1.9rem)] font-bold leading-[1.15] tracking-[-0.02em]">
              {p.title}
            </h3>
            <p className="mt-4 max-w-sm text-[15px] leading-[1.8] text-white/65">{p.body}</p>
          </Rise>
        ))}
      </div>

      <Rise delay={120} className="mt-20 sm:mt-28">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/60">Inside Xamseen</p>
        <p className="mt-5 max-w-3xl font-display text-[clamp(1.1rem,4vw,1.6rem)] font-semibold leading-[1.5] tracking-[-0.015em]">
          {SURFACES.join("  ·  ")}
        </p>
      </Rise>
    </section>
  );
}


/* --------------------------------- Stories -------------------------------- */

const STORIES = [
  {
    img: docReport,
    alt: "A woman photographing a large water-filled pothole on a residential street in Bogotá, Colombia",
    eyebrow: "Observation",
    title: "Something nobody recorded",
    body: "A pothole, an unsafe crossing, a dark stretch of road. One person posts it with a photo and a place.",
  },
  {
    img: docMeeting,
    alt: "Neighbours seated in a community hall in Rotterdam discussing a local issue",
    eyebrow: "Conversation",
    title: "Neighbours weigh in",
    body: "Others confirm it, add what they know and talk it out in a Space for that place.",
  },
  {
    img: docRepair,
    alt: "Municipal workers repairing a leaking water pipe in Nairobi while a resident films on a phone",
    eyebrow: "Outcome",
    title: "Kept in the open",
    body: "Responses, work and resolutions stay visible, so anyone can see what changed.",
  },
];

function StoriesSection() {
  const reduced = useReducedMotion();
  return (
    <section id="stories" className="mx-auto w-full max-w-7xl scroll-mt-24 px-4 py-20 sm:px-8 sm:py-32">
      <SectionHead icon={Newspaper} eyebrow="Stories" title="What using Xamseen looks like.">
        <p className="mt-5 max-w-xl text-[15px] leading-[1.8] text-white/65">
          How a single observation travels — from one street to a response anyone can follow.
        </p>
      </SectionHead>

      <div className="mt-16 space-y-20 sm:mt-24 sm:space-y-32">
        {STORIES.map((s, i) => (
          <div
            key={s.title}
            className={cn(
              "grid items-center gap-8 lg:grid-cols-2 lg:gap-16",
              i % 2 === 1 && "lg:[&>*:first-child]:order-2",
            )}
          >
            <div className="overflow-hidden">
              <motion.img
                src={s.img}
                alt={s.alt}
                width={1200}
                height={912}
                loading="lazy"
                decoding="async"
                initial={reduced ? undefined : { scale: 1.1, opacity: 0 }}
                whileInView={reduced ? undefined : { scale: 1, opacity: 1 }}
                viewport={{ once: true, margin: "0px 0px -8% 0px" }}
                transition={{ duration: 1.2, ease: EASE }}
                className="h-64 w-full object-cover sm:h-[26rem]"
              />
            </div>
            <Rise delay={100}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/60">{s.eyebrow}</p>
              <h3 className="mt-5 max-w-md font-display text-[clamp(1.5rem,5.5vw,2.3rem)] font-bold leading-[1.12] tracking-[-0.02em]">
                {s.title}
              </h3>
              <p className="mt-5 max-w-md text-[15px] leading-[1.8] text-white/65">{s.body}</p>
            </Rise>
          </div>
        ))}
      </div>
    </section>
  );
}


/* ----------------------------- Impact stories ----------------------------- */

/**
 * Editorial examples of public projects run elsewhere, each credited and linked
 * to the body that published it. These are not Xamseen records.
 */
function ImpactStoriesSection() {
  const [stories, setStories] = useState<ImpactStory[]>(IMPACT_STORIES);

  useEffect(() => {
    let alive = true;
    void fetchPublishedImpactStories().then((s) => { if (alive) setStories(s); });
    return () => { alive = false; };
  }, []);

  return (
    <section id="impact" className="mx-auto w-full max-w-7xl scroll-mt-24 px-4 py-20 sm:px-8 sm:py-32">
      <SectionHead icon={Target} eyebrow="Impact" title="Public work, kept in public view.">
        <p className="mt-5 max-w-xl text-[15px] leading-[1.8] text-white/65">
          Verified offices and organisations keep a record of their projects on Xamseen, from the problem that prompted
          them through to the community&apos;s verdict. Below are real projects published by public bodies elsewhere —
          credited to their own sources, not Xamseen records.
        </p>
      </SectionHead>

      <div className="mt-16 grid gap-14 sm:mt-20 lg:grid-cols-3 lg:gap-x-14">
        {stories.map((s, i) => (
          <Rise key={s.title} delay={i * 90} className="group flex flex-col">
            <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">
              <MapPin className="h-3.5 w-3.5" /> {s.place}, {s.country}
            </p>
            <h3 className="mt-4 font-display text-[clamp(1.2rem,4vw,1.5rem)] font-bold leading-[1.2] tracking-[-0.015em]">
              {s.title}
            </h3>
            <p className="mt-4 flex-1 text-[14px] leading-[1.8] text-white/65">{s.body}</p>
            <p className="mt-5 text-[13px] text-white/60">{s.responsible}</p>
            <a
              href={s.source.url}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-2 inline-flex min-h-11 items-center gap-1.5 text-[13px] font-medium text-white/70 hover:text-white"
            >
              Source: {s.source.publisher}
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
            </a>
          </Rise>
        ))}
      </div>
    </section>

  );
}


/* --------------------------------- Journey -------------------------------- */

const STEPS = [
  {
    icon: Eye,
    title: "Observation",
    body: "You notice something and post it — photos, a short description and a place you control.",
    meta: "Photo · Place · Detail",
  },
  {
    icon: MessagesSquare,
    title: "Conversation",
    body: "Neighbours confirm it, add detail and talk about it in the Space for that place.",
    meta: "Confirmations · Context",
  },
  {
    icon: ClipboardCheck,
    title: "Solution",
    body: "From a problem to a project: Answers and Projects turn the talk into a plan, with people attached to it.",
    meta: "Plan · Owners · Milestones",
  },
  {
    icon: CheckCircle2,
    title: "Impact",
    body: "See the work. Follow the progress. Updates and resolutions stay on the record long after the report.",
    meta: "Updates · Resolution · Record",
  },
];

/**
 * One stage of the report journey. The rail and the marker are the only lines on
 * the page, because here they carry meaning: they show how far along the path
 * the reader is. The active stage lights up as the section scrolls.
 */
function Step({
  step,
  index,
  active,
  reduced,
}: {
  step: (typeof STEPS)[number];
  index: number;
  active: boolean;
  reduced: boolean | null;
}) {
  const Icon = step.icon;
  return (
    <motion.li
      initial={reduced ? undefined : { opacity: 0, y: 26 }}
      whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.5 }}
      transition={{ duration: 0.7, delay: index * 0.09, ease: [0.22, 1, 0.36, 1] }}
      className="relative pl-16 sm:pl-20"
    >
      <motion.div
        animate={reduced ? undefined : { opacity: active ? 1 : 0.5 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="py-3 sm:py-4"
      >
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60">{step.meta}</p>
        <h3 className="mt-3 font-display text-[clamp(1.3rem,4.5vw,1.85rem)] font-bold leading-[1.15] tracking-[-0.02em]">
          {step.title}
        </h3>
        <p className="mt-3 max-w-md text-[15px] leading-[1.8] text-white/65">{step.body}</p>
      </motion.div>

      <motion.span
        aria-hidden
        animate={
          reduced
            ? undefined
            : {
                scale: active ? 1 : 0.9,
                backgroundColor: active ? "rgb(255,255,255)" : "rgba(11,11,11,1)",
                color: active ? "rgb(0,0,0)" : "rgba(255,255,255,0.7)",
              }
        }
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        className="absolute left-0 top-6 grid h-11 w-11 place-items-center rounded-2xl bg-[#0b0b0b] text-white/75 sm:h-14 sm:w-14"
      >
        <Icon className="h-5 w-5 sm:h-[1.4rem] sm:w-[1.4rem]" strokeWidth={1.6} />
      </motion.span>
    </motion.li>
  );
}

function Journey() {
  const reduced = useReducedMotion();
  const railRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: railRef, offset: ["start 78%", "end 55%"] });
  const draw = useSpring(scrollYProgress, { stiffness: 90, damping: 26, mass: 0.4 });
  const scaleY = useTransform(reduced ? scrollYProgress : draw, (v) => (reduced ? 1 : v));
  const [active, setActive] = useState(0);

  useMotionValueEvent(draw, "change", (v) => {
    const next = Math.max(0, Math.min(STEPS.length - 1, Math.floor(v * STEPS.length + 0.0001)));
    setActive((a) => (a === next ? a : next));
  });

  return (
    <section id="journey" className="mx-auto w-full max-w-7xl scroll-mt-24 px-4 py-20 sm:px-8 sm:py-32">
      <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
        <Rise className="lg:sticky lg:top-28 lg:self-start">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/60">How it works</p>
          <h2 className="mt-6 font-display text-[clamp(2rem,7vw,3.4rem)] font-black leading-[1.05] tracking-[-0.025em]">
            From a report to a response.
          </h2>
          <p className="mt-6 max-w-md text-[15px] leading-[1.8] text-white/65">
            Xamseen keeps the whole path in one place, so nothing stops at a complaint.
          </p>
          <p className="mt-4 max-w-md text-[13px] leading-relaxed text-white/60">
            Xamseen is not an emergency service. For emergencies, contact your local services.
          </p>
        </Rise>

        <div ref={railRef} className="relative">
          {/* The journey path: a faint track and a line that fills as the reader advances. */}
          <span className="pointer-events-none absolute left-[21px] top-10 bottom-10 w-px bg-white/[0.08] sm:left-[27px]" />
          <motion.span
            style={{ scaleY }}
            className="pointer-events-none absolute left-[21px] top-10 bottom-10 w-px origin-top bg-white/70 sm:left-[27px]"
          />
          <ol className="relative space-y-10 sm:space-y-14">
            {STEPS.map((s, i) => (
              <Step key={s.title} step={s} index={i} active={reduced ? true : i <= active} reduced={reduced} />
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}


/* -------------------------------- Principle ------------------------------- */

const TRUST = [
  { title: "No data brokering", body: "Xamseen does not sell, rent or broker personal data." },
  { title: "You set the precision", body: "Choose how precisely your location is shared on every report." },
  { title: "Used to run Xamseen", body: "Platform data is used to run the service and to show communities and responsible authorities what is happening in an area." },
  { title: "Open by default", body: "Responses and resolutions stay visible, so anyone can see the work and follow the progress." },
];

function Principle() {
  return (
    <section id="principles" className="mx-auto w-full max-w-7xl scroll-mt-24 px-4 py-20 sm:px-8 sm:py-32">
      <div className="grid gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
        <SectionHead
          icon={Lock}
          eyebrow="Privacy & data"
          title="Your data, your boundaries."
        >
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-1 text-[13px] text-white/60">
            <Link to="/privacy" className="inline-flex min-h-11 items-center underline hover:text-white">Privacy Policy</Link>
            <Link to="/data-use" className="inline-flex min-h-11 items-center underline hover:text-white">How Xamseen Uses Data</Link>
            <Link to="/community-guidelines" className="inline-flex min-h-11 items-center underline hover:text-white">Community Guidelines</Link>
          </div>
        </SectionHead>
        <div className="grid gap-x-12 gap-y-10 sm:grid-cols-2">
          {TRUST.map((t, i) => (
            <Rise key={t.title} delay={i * 70}>
              <h3 className="font-display text-[15px] font-bold uppercase tracking-[0.1em]">{t.title}</h3>
              <p className="mt-3 text-[14px] leading-[1.8] text-white/65">{t.body}</p>
            </Rise>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ----------------------------------- FAQ ---------------------------------- */

const FAQS = [
  {
    question: "What is Xamseen?",
    answer: "Xamseen is a place to share what is happening around you, talk about it with people nearby, and follow what happens next.",
  },
  {
    question: "How do I report something?",
    answer: "Write what you saw, add photos or a video if you have them, and set the place. It takes a minute.",
  },
  {
    question: "Who can use Xamseen?",
    answer: "Anyone. Residents, community groups, and verified officials and organisations.",
  },
  {
    question: "Can I upload photos and videos?",
    answer: "Yes. You can add photos, video, voice notes and documents to a post or a report.",
  },
  {
    question: "Is my information private?",
    answer: "You choose how precisely your location is shared, and who can see your profile. Xamseen does not sell, rent or broker personal data. You can export or delete your account whenever you want.",
  },
  {
    question: "How are officials verified?",
    answer: "Public offices and organisations are checked before they get a verified badge, so you know who is answering.",
  },
  {
    question: "What happens after I report an issue?",
    answer: "Neighbours can confirm it and add detail, it appears on the map, and any response, work or resolution is recorded on the report so you can see what changed.",
  },
];

function FaqSection() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section id="faq" className="mx-auto w-full max-w-7xl scroll-mt-24 px-4 py-20 sm:px-8 sm:py-32">
      <SectionHead icon={HelpCircle} eyebrow="FAQ" title="Questions about Xamseen?" />

      <div className="mt-14 grid max-w-3xl gap-8 sm:mt-20 sm:gap-10">
        {FAQS.map((item, i) => {
          const isOpen = open === i;
          return (
            <Rise key={item.question} delay={i * 60} as="div">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                className="flex min-h-11 w-full items-start gap-5 text-left"
                aria-expanded={isOpen}
              >
                <span className="flex-1 font-display text-[clamp(1.05rem,3.6vw,1.35rem)] font-semibold leading-snug tracking-[-0.01em]">
                  {item.question}
                </span>
                <motion.span
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: 0.25, ease: EASE }}
                  className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center text-white/60"
                >
                  <ChevronDown className="h-5 w-5" strokeWidth={1.6} />
                </motion.span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <p className="max-w-2xl pt-4 text-[15px] leading-[1.8] text-white/65">{item.answer}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </Rise>
          );
        })}
      </div>
    </section>
  );
}


/* -------------------------------- Final CTA ------------------------------- */

function FinalCta() {
  return (
    <section id="join" className="mx-auto w-full max-w-7xl scroll-mt-24 px-4 pt-24 pb-20 sm:px-8 sm:pt-32 sm:pb-28">
      <Rise className="pb-16 text-center sm:pb-24">
        <h2 className="font-display text-[clamp(2.2rem,10vw,5.5rem)] font-black uppercase leading-[0.95] tracking-[-0.035em]">
          See it.
          <br />
          Share it.
          <br />
          Shape it.
        </h2>
      </Rise>
      <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
        <Rise className="grid grid-cols-2 gap-3 self-start">
          <img src={docCleanup} alt="Residents clearing rubble from a vacant lot in Amman, Jordan" width={1200} height={912} loading="lazy" decoding="async" className="col-span-2 h-48 w-full object-cover sm:h-72" />
          <img src={docMeeting} alt="A neighbourhood meeting in a community hall in Rotterdam" width={1200} height={912} loading="lazy" decoding="async" className="h-32 w-full object-cover sm:h-44" />
          <img src={docTransit} alt="Commuters at a bus shelter in Chicago" width={1200} height={912} loading="lazy" decoding="async" className="h-32 w-full object-cover sm:h-44" />
        </Rise>
        <Rise delay={100} className="max-w-md">
          <h3 className="font-display text-[clamp(1.6rem,6vw,2.4rem)] font-black leading-[1.05] tracking-[-0.02em]">
            Join Xamseen.
          </h3>
          <p className="mt-5 text-[15px] leading-relaxed text-white/65">
            Your street, your city, your country — what you see matters.
          </p>
          <InlineSignup />
        </Rise>
      </div>
    </section>

  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function InlineSignup() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthPending, setOauthPending] = useState<"google" | "apple" | null>(null);
  const [oauthFailure, setOauthFailure] = useState<OAuthFailure | null>(null);
  const [lastProvider, setLastProvider] = useState<"google" | "apple" | null>(null);

  // Apple/Google send cancellations back as ?error=access_denied on this page.
  useEffect(() => {
    const fromUrl = consumeOAuthErrorFromUrl();
    if (fromUrl) setOauthFailure(fromUrl);
  }, []);



  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError("Please enter your full name.");
    if (!EMAIL_RE.test(email.trim())) return setError("Please enter a valid email address.");
    if (password.length < 8) return setError("Use a password of at least 8 characters.");
    setError(null);
    setBusy(true);
    const [first, ...rest] = name.trim().split(/\s+/);
    const { error: err } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/welcome`,
        data: {
          first_name: first,
          last_name: rest.join(" ") || null,
          full_name: name.trim(),
        },
      },
    });
    setBusy(false);
    if (err) {
      setError(
        /already registered|already been registered/i.test(err.message)
          ? "An account with this email already exists. Try signing in instead."
          : err.message
      );
      return;
    }
    navigate({ to: "/verify-email" });
  };

  const oauth = async (provider: "google" | "apple") => {
    if (oauthPending) return;
    setOauthPending(provider);
    setLastProvider(provider);
    setOauthFailure(null);
    const result = await lovable.auth.signInWithOAuth(provider, { redirect_uri: window.location.origin });
    if (result.error) {
      setOauthFailure(describeOAuthError(provider, result.error.message));
      setOauthPending(null);
    }
  };


  return (
    <div className="mt-8">
      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">Create your free account</p>
      <form onSubmit={submit} noValidate className="mt-4 grid gap-3">
        {error && (
          <p className="rounded-xl border border-destructive/40 bg-destructive/10 px-3.5 py-2.5 text-[13px] text-destructive">
            {error}
          </p>
        )}
        <Field icon={User} label="Full name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            autoComplete="name"
            aria-label="Full name"
            className="h-full w-full min-w-0 bg-transparent text-[15px] outline-none placeholder:text-white/60"
          />
        </Field>
        <Field icon={Mail} label="Email address">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            autoComplete="email"
            aria-label="Email address"
            className="h-full w-full min-w-0 bg-transparent text-[15px] outline-none placeholder:text-white/60"
          />
        </Field>
        <Field icon={KeyRound} label="Password">
          <input
            type={show ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            autoComplete="new-password"
            aria-label="Password"
            className="h-full w-full min-w-0 bg-transparent text-[15px] outline-none placeholder:text-white/60"
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            aria-label={show ? "Hide password" : "Show password"}
            className="-mr-2 grid h-11 w-11 shrink-0 place-items-center text-white/60 hover:text-white"
          >
            {show ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
          </button>
        </Field>

        <motion.button
          type="submit"
          disabled={busy}
          whileTap={{ scale: 0.99 }}
          className="mt-1 inline-flex min-h-13 items-center justify-center gap-2 rounded-xl bg-white py-4 text-[15px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Join Xamseen
        </motion.button>
      </form>

      <div className="my-5 flex items-center gap-4">
        <span className="h-px flex-1 bg-white/10" />
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/60">or continue with</span>
        <span className="h-px flex-1 bg-white/10" />
      </div>

      <div className="grid gap-3">
        {oauthFailure && (
          <div
            role="status"
            aria-live="polite"
            className={cn(
              "rounded-xl border px-3.5 py-3 text-[13px]",
              oauthFailure.kind === "cancelled"
                ? "border-white/15 bg-white/[0.05] text-white/80"
                : "border-destructive/45 bg-destructive/15 text-white/85"
            )}
          >
            <p className="font-semibold text-white">{oauthFailure.title}</p>
            <p className="mt-0.5">{oauthFailure.message}</p>
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              {oauthFailure.retryable && lastProvider && (
                <button
                  type="button"
                  onClick={() => oauth(lastProvider)}
                  disabled={oauthPending !== null}
                  className="inline-flex items-center rounded-lg bg-white px-3 py-1.5 text-[12px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-60"
                >
                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Try again
                </button>
              )}
              <button
                type="button"
                onClick={() => setOauthFailure(null)}
                className="rounded-lg px-2.5 py-1.5 text-[12px] font-medium text-white/65 hover:text-white"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        {([
          { id: "google", label: "Google", Mark: GoogleMark },
          { id: "apple", label: "Apple", Mark: AppleMark },
        ] as const).map(({ id, label, Mark }) => (
          <button
            key={id}
            type="button"
            onClick={() => oauth(id)}
            disabled={oauthPending !== null || busy}
            aria-busy={oauthPending === id}
            aria-label={`Continue with ${label}`}
            className="inline-flex min-h-13 w-full items-center justify-center rounded-xl border border-white/15 py-3.5 text-[15px] font-medium transition-colors hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
          >
            {oauthPending === id ? <Loader2 className="mr-2.5 h-4 w-4 shrink-0 animate-spin" /> : <Mark />} Continue with {label}
          </button>
        ))}
      </div>



      <p className="mt-5 text-sm text-white/65">
        Already have an account?{" "}
        <Link to="/signin" className="font-medium text-white underline">Sign in</Link>
      </p>
      <p className="mt-3 text-[12px] leading-relaxed text-white/60">
        By creating an account you agree to the{" "}
        <Link to="/terms" className="underline hover:text-white/70">Terms of Service</Link>,{" "}
        <Link to="/community-guidelines" className="underline hover:text-white/70">Community Guidelines</Link> and{" "}
        <Link to="/privacy" className="underline hover:text-white/70">Privacy Policy</Link>. Xamseen is not an
        emergency service.
      </p>
    </div>
  );
}

function Field({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Mail;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label aria-label={label} className="flex min-h-13 items-center gap-3 rounded-xl border border-white/12 bg-white/[0.03] px-4 py-3.5 focus-within:border-white/35">
      <Icon className="h-[18px] w-[18px] shrink-0 text-white/60" />
      {children}
    </label>
  );
}

/* --------------------------------- Footer -------------------------------- */

const FOOTER_LINKS: { to: string; label: string }[] = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms of Service" },
  { to: "/data-use", label: "How Xamseen Uses Data" },
  { to: "/community-guidelines", label: "Community Guidelines" },
  { to: "/accessibility", label: "Accessibility" },
  { to: "/contact", label: "Contact" },
];

function PublicFooter() {
  return (
    <footer className="mx-auto w-full max-w-7xl px-4 pb-12 sm:px-8">
      <div className="flex flex-col gap-6 pt-8 lg:flex-row lg:items-center lg:justify-between">
        <XamseenLogo variant="white" className="h-5" />
        <nav aria-label="Footer" className="flex flex-wrap gap-x-7 gap-y-3 text-[13px] text-white/65">
          {FOOTER_LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              className="inline-flex min-h-11 items-center hover:text-white"
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <p className="text-[12px] text-white/60">© {new Date().getFullYear()} Xamseen · xamseen.com</p>
      </div>
    </footer>
  );
}
